/**
 * Which property a farm-system quote is for (#36618).
 *
 * A mutual member can hold several quotes of the same policy type at different properties: a
 * primary and a seasonal Home, sixteen Rentals, three Farms (CFM writes one dwelling per policy).
 * Every quote for one person shares a webUserId, and the policy-type product set (#34790) can't tell
 * two FarmHome (Z) quotes apart, so staff offer search showed one of them and the "most recent saved
 * changes" redirect kept turning one into the other. The property is what separates them.
 *
 * Shared by the offer-history search mapper (the dedupe key) and both redirect paths (staff-fe and
 * staff-appsync-be), which must agree on what "the same property" means.
 */
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

export type PropertyAddress = {
  address?: string | null;
  address2?: string | null;
  unit?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

type OfferWithProperty = {
  quote?: {
    submittedAddress?: PropertyAddress | null;
    farmLocations?: Array<{ _ref?: boolean; address?: PropertyAddress | null } | null> | null;
  } | null;
};

const normalize = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/**
 * The address of the property a quote covers: its primary farm location, else (an in-progress quote
 * with no location address yet, or a non-farm quote) the submitted address. The same rule the
 * offer-history mapper uses for the indexed `quote.submittedAddress`. A `_ref` stub is never read:
 * farm locations are extracted entities and carry no address until hydrated.
 */
export function offerPropertyAddress(offer: OfferWithProperty | null | undefined): PropertyAddress | null {
  const primaryLocation = offer?.quote?.farmLocations?.[0];
  if (primaryLocation && !primaryLocation._ref && primaryLocation.address) {
    return primaryLocation.address;
  }
  return offer?.quote?.submittedAddress ?? null;
}

/**
 * The property as a comparable key: street, unit, city, state and 5-digit zip, case- and
 * whitespace-normalised. `unit` and `address2` are one field (forms use either). Null without a
 * street, since a city alone does not identify a property.
 */
export function offerPropertyKey(address: PropertyAddress | null | undefined): string | null {
  const street = normalize(address?.address);
  if (!street) {
    return null;
  }
  const unit = normalize(address?.unit) || normalize(address?.address2);
  const zip = normalize(address?.zip).slice(0, 5);
  return [street, unit, normalize(address?.city), normalize(address?.state), zip].join('|');
}

/** The offer-history attribute holding each offer's property address (see `offerPropertyAddress`). */
export const OFFER_PROPERTY_STREET_ATTRIBUTE = 'quote.submittedAddress.address';

/**
 * The street the redirect lookups pre-filter MeiliSearch on (equality against
 * `OFFER_PROPERTY_STREET_ATTRIBUTE`), so the other property's revisions can't fill the candidate page;
 * `offerPropertyKey` then decides. Trimmed but otherwise raw, unlike the key: the filter is exact, and
 * that holds because every revision of a quote carries the same stored address. Null without a street.
 */
export function offerPropertyStreet(address: PropertyAddress | null | undefined): string | null {
  const street = String(address?.address ?? '').trim();
  return street || null;
}

/** Farm-system products (F, Z, Y) are written one property per quote, so the property tells quotes apart. */
export function isPropertyScopedProductSet(productSet: string[]): boolean {
  return productSet.some((type) => isFarmBasedPolicyType(type));
}
